// src/features/employees/employeeSlice.js
import { createSlice } from '@reduxjs/toolkit';
import {
  fetchEmployeesThunk,
  fetchEmployeeThunk,
  createEmployeeThunk,
  updateEmployeeThunk,
  fetchEmployeeWithTargetsThunk,
  bulkCreateEmployeesThunk,
  resetEmployeePasswordThunk,
  // Enhanced incentive thunks
  fetchEmployeeIncentiveSummaryThunk,
  fetchEnhancedEmployeeIncentiveSummaryThunk,
  fetchDetailedIncentiveHistoryThunk,
  fetchIncentiveTransactionsThunk,
  fetchMonthlyIncentivesThunk,
  fetchIncentiveTransactionsSummaryThunk,
  bulkUpdateTransactionStatusThunk,
  updateMonthlyIncentiveStatusThunk,
  fetchPendingMonthlyIncentivesSummaryThunk,
  calculateMonthlyIncentivesThunk,
  // Admin thunks
  fetchAllEmployeesIncentivesSummaryThunk,
  fetchMonthlyIncentivesDashboardThunk
} from './employeeThunks';

const initialState = {
  employees: [],
  currentEmployee: null,
  loading: false,
  error: null,
  
  // Employee creation response data
  lastCreationResponse: null,
  
  // Basic incentive summary (original)
  incentiveSummary: {
    data: null,
    loading: false,
    error: null,
  },
  
  // Enhanced incentive summary with monthly breakdown
  enhancedIncentiveSummary: {
    data: null,
    loading: false,
    error: null,
  },
  
  // Detailed incentive history with pagination
  detailedIncentiveHistory: {
    list: [],
    loading: false,
    error: null,
    next: null,
    previous: null,
    count: 0,
  },
  
  // Incentive transactions
  incentiveTransactions: {
    list: [],
    loading: false,
    error: null,
    next: null,
    count: 0,
    summary: null,
  },
  
  // Monthly incentives
  monthlyIncentives: {
    list: [],
    loading: false,
    error: null,
    next: null,
    count: 0,
    pendingSummary: null,
  },
  
  // Admin-specific data
  allEmployeesIncentivesSummary: {
    data: null,
    loading: false,
    error: null,
  },
  
  monthlyIncentivesDashboard: {
    data: null,
    loading: false,
    error: null,
  },
};

const employeeSlice = createSlice({
  name: 'employees',
  initialState,
  reducers: {
    // Basic employee actions
    clearCurrentEmployee(state) {
      state.currentEmployee = null;
    },
    
    clearCreationResponse(state) {
      state.lastCreationResponse = null;
    },
    
    clearEmployeeErrors(state) {
      state.error = null;
    },
    
    // Incentive data clearing actions
    clearIncentiveSummary(state) {
      state.incentiveSummary = { ...initialState.incentiveSummary };
    },
    
    clearEnhancedIncentiveSummary(state) {
      state.enhancedIncentiveSummary = { ...initialState.enhancedIncentiveSummary };
    },
    
    clearDetailedIncentiveHistory(state) {
      state.detailedIncentiveHistory = { ...initialState.detailedIncentiveHistory };
    },
    
    clearIncentiveTransactions(state) {
      state.incentiveTransactions = { ...initialState.incentiveTransactions };
    },
    
    clearMonthlyIncentives(state) {
      state.monthlyIncentives = { ...initialState.monthlyIncentives };
    },
    
    clearAllEmployeesIncentivesSummary(state) {
      state.allEmployeesIncentivesSummary = { ...initialState.allEmployeesIncentivesSummary };
    },
    
    clearMonthlyIncentivesDashboard(state) {
      state.monthlyIncentivesDashboard = { ...initialState.monthlyIncentivesDashboard };
    },
    
    // Reset all incentive data
    resetAllIncentiveData(state) {
      state.incentiveSummary = { ...initialState.incentiveSummary };
      state.enhancedIncentiveSummary = { ...initialState.enhancedIncentiveSummary };
      state.detailedIncentiveHistory = { ...initialState.detailedIncentiveHistory };
      state.incentiveTransactions = { ...initialState.incentiveTransactions };
      state.monthlyIncentives = { ...initialState.monthlyIncentives };
      state.allEmployeesIncentivesSummary = { ...initialState.allEmployeesIncentivesSummary };
      state.monthlyIncentivesDashboard = { ...initialState.monthlyIncentivesDashboard };
    },
    
    // Update individual transaction status
    updateTransactionStatus(state, action) {
      const { transactionId, status } = action.payload;
      state.incentiveTransactions.list = state.incentiveTransactions.list.map(transaction => 
        transaction.id === transactionId 
          ? { ...transaction, status, processed_at: new Date().toISOString() }
          : transaction
      );
      
      state.detailedIncentiveHistory.list = state.detailedIncentiveHistory.list.map(transaction => 
        transaction.id === transactionId 
          ? { ...transaction, status, processed_at: new Date().toISOString() }
          : transaction
      );
    },
  },
  extraReducers: (builder) => {
    builder
      // =============================================================
      // BASIC EMPLOYEE OPERATIONS
      // =============================================================
      
      // Fetch all employees
      .addCase(fetchEmployeesThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEmployeesThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.employees = action.payload;
      })
      .addCase(fetchEmployeesThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch single employee
      .addCase(fetchEmployeeThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.currentEmployee = null;
      })
      .addCase(fetchEmployeeThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.currentEmployee = action.payload;
      })
      .addCase(fetchEmployeeThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create employee
      .addCase(createEmployeeThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.lastCreationResponse = null;
      })
      .addCase(createEmployeeThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.lastCreationResponse = action.payload;
        const employeeData = action.payload.employee || action.payload;
        state.employees.push(employeeData);
      })
      .addCase(createEmployeeThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.lastCreationResponse = null;
      })

      // Update employee
      .addCase(updateEmployeeThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateEmployeeThunk.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload;
        const idx = state.employees.findIndex((emp) => emp.id === updated.id);
        if (idx !== -1) {
          state.employees[idx] = updated;
        }
        if (state.currentEmployee && state.currentEmployee.id === updated.id) {
          state.currentEmployee = updated;
        }
      })
      .addCase(updateEmployeeThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch employee with targets
      .addCase(fetchEmployeeWithTargetsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.currentEmployee = null;
      })
      .addCase(fetchEmployeeWithTargetsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.currentEmployee = action.payload;
      })
      .addCase(fetchEmployeeWithTargetsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Bulk create employees
      .addCase(bulkCreateEmployeesThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkCreateEmployeesThunk.fulfilled, (state, action) => {
        state.loading = false;
        const createdEmployees = action.payload.employees || [];
        state.employees.push(...createdEmployees);
        state.lastCreationResponse = action.payload;
      })
      .addCase(bulkCreateEmployeesThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Reset employee password
      .addCase(resetEmployeePasswordThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetEmployeePasswordThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.lastCreationResponse = action.payload;
      })
      .addCase(resetEmployeePasswordThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // =============================================================
      // BASIC INCENTIVE SUMMARY (Original)
      // =============================================================
      
      .addCase(fetchEmployeeIncentiveSummaryThunk.pending, (state) => {
        state.incentiveSummary.loading = true;
        state.incentiveSummary.error = null;
      })
      .addCase(fetchEmployeeIncentiveSummaryThunk.fulfilled, (state, action) => {
        state.incentiveSummary.loading = false;
        state.incentiveSummary.data = action.payload;
      })
      .addCase(fetchEmployeeIncentiveSummaryThunk.rejected, (state, action) => {
        state.incentiveSummary.loading = false;
        state.incentiveSummary.error = action.payload;
      })

      // =============================================================
      // ENHANCED INCENTIVE SUMMARY
      // =============================================================
      
      .addCase(fetchEnhancedEmployeeIncentiveSummaryThunk.pending, (state) => {
        state.enhancedIncentiveSummary.loading = true;
        state.enhancedIncentiveSummary.error = null;
      })
      .addCase(fetchEnhancedEmployeeIncentiveSummaryThunk.fulfilled, (state, action) => {
        state.enhancedIncentiveSummary.loading = false;
        state.enhancedIncentiveSummary.data = action.payload;
      })
      .addCase(fetchEnhancedEmployeeIncentiveSummaryThunk.rejected, (state, action) => {
        state.enhancedIncentiveSummary.loading = false;
        state.enhancedIncentiveSummary.error = action.payload;
      })

      // =============================================================
      // DETAILED INCENTIVE HISTORY
      // =============================================================
      
      .addCase(fetchDetailedIncentiveHistoryThunk.pending, (state) => {
        state.detailedIncentiveHistory.loading = true;
        state.detailedIncentiveHistory.error = null;
      })
      .addCase(fetchDetailedIncentiveHistoryThunk.fulfilled, (state, action) => {
        state.detailedIncentiveHistory.loading = false;
        const { results, next, previous, count } = action.payload;
        const offset = action.meta.arg?.offset ?? 0;
        
        if (offset > 0) {
          // Append for pagination
          state.detailedIncentiveHistory.list.push(...results);
        } else {
          // Replace for new fetch
          state.detailedIncentiveHistory.list = results;
        }
        
        state.detailedIncentiveHistory.next = next;
        state.detailedIncentiveHistory.previous = previous;
        state.detailedIncentiveHistory.count = count;
      })
      .addCase(fetchDetailedIncentiveHistoryThunk.rejected, (state, action) => {
        state.detailedIncentiveHistory.loading = false;
        state.detailedIncentiveHistory.error = action.payload;
      })

      // =============================================================
      // INCENTIVE TRANSACTIONS
      // =============================================================
      
      .addCase(fetchIncentiveTransactionsThunk.pending, (state) => {
        state.incentiveTransactions.loading = true;
        state.incentiveTransactions.error = null;
      })
      .addCase(fetchIncentiveTransactionsThunk.fulfilled, (state, action) => {
        state.incentiveTransactions.loading = false;
        const { results, next, count } = action.payload;
        const offset = action.meta.arg?.offset ?? 0;
        
        if (offset > 0) {
          state.incentiveTransactions.list.push(...results);
        } else {
          state.incentiveTransactions.list = results;
        }
        
        state.incentiveTransactions.next = next;
        state.incentiveTransactions.count = count;
      })
      .addCase(fetchIncentiveTransactionsThunk.rejected, (state, action) => {
        state.incentiveTransactions.loading = false;
        state.incentiveTransactions.error = action.payload;
      })

      // Incentive transactions summary
      .addCase(fetchIncentiveTransactionsSummaryThunk.pending, (state) => {
        state.incentiveTransactions.loading = true;
      })
      .addCase(fetchIncentiveTransactionsSummaryThunk.fulfilled, (state, action) => {
        state.incentiveTransactions.loading = false;
        state.incentiveTransactions.summary = action.payload;
      })
      .addCase(fetchIncentiveTransactionsSummaryThunk.rejected, (state, action) => {
        state.incentiveTransactions.loading = false;
        state.incentiveTransactions.error = action.payload;
      })

      // =============================================================
      // MONTHLY INCENTIVES
      // =============================================================
      
      .addCase(fetchMonthlyIncentivesThunk.pending, (state) => {
        state.monthlyIncentives.loading = true;
        state.monthlyIncentives.error = null;
      })
      .addCase(fetchMonthlyIncentivesThunk.fulfilled, (state, action) => {
        state.monthlyIncentives.loading = false;
        const { results, next, count } = action.payload;
        const offset = action.meta.arg?.offset ?? 0;
        
        if (offset > 0) {
          state.monthlyIncentives.list.push(...results);
        } else {
          state.monthlyIncentives.list = results;
        }
        
        state.monthlyIncentives.next = next;
        state.monthlyIncentives.count = count;
      })
      .addCase(fetchMonthlyIncentivesThunk.rejected, (state, action) => {
        state.monthlyIncentives.loading = false;
        state.monthlyIncentives.error = action.payload;
      })

      // Pending monthly incentives summary
      .addCase(fetchPendingMonthlyIncentivesSummaryThunk.pending, (state) => {
        state.monthlyIncentives.loading = true;
      })
      .addCase(fetchPendingMonthlyIncentivesSummaryThunk.fulfilled, (state, action) => {
        state.monthlyIncentives.loading = false;
        state.monthlyIncentives.pendingSummary = action.payload;
      })
      .addCase(fetchPendingMonthlyIncentivesSummaryThunk.rejected, (state, action) => {
        state.monthlyIncentives.loading = false;
        state.monthlyIncentives.error = action.payload;
      })

      // =============================================================
      // BULK OPERATIONS
      // =============================================================
      
      .addCase(bulkUpdateTransactionStatusThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkUpdateTransactionStatusThunk.fulfilled, (state, action) => {
        state.loading = false;
        const { transaction_ids, status } = action.meta.arg;
        
        // Update transaction statuses in local state
        state.incentiveTransactions.list = state.incentiveTransactions.list.map(transaction => {
          if (transaction_ids.includes(transaction.id)) {
            return { ...transaction, status, processed_at: new Date().toISOString() };
          }
          return transaction;
        });
        
        state.detailedIncentiveHistory.list = state.detailedIncentiveHistory.list.map(transaction => {
          if (transaction_ids.includes(transaction.id)) {
            return { ...transaction, status, processed_at: new Date().toISOString() };
          }
          return transaction;
        });
      })
      .addCase(bulkUpdateTransactionStatusThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update monthly incentive status
      .addCase(updateMonthlyIncentiveStatusThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateMonthlyIncentiveStatusThunk.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload;
        const idx = state.monthlyIncentives.list.findIndex(item => item.id === updated.id);
        if (idx !== -1) {
          state.monthlyIncentives.list[idx] = updated;
        }
      })
      .addCase(updateMonthlyIncentiveStatusThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Calculate monthly incentives
      .addCase(calculateMonthlyIncentivesThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(calculateMonthlyIncentivesThunk.fulfilled, (state, action) => {
        state.loading = false;
        // Could trigger a refresh of monthly incentives data
      })
      .addCase(calculateMonthlyIncentivesThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // =============================================================
      // ADMIN INCENTIVE DATA
      // =============================================================
      
      .addCase(fetchAllEmployeesIncentivesSummaryThunk.pending, (state) => {
        state.allEmployeesIncentivesSummary.loading = true;
        state.allEmployeesIncentivesSummary.error = null;
      })
      .addCase(fetchAllEmployeesIncentivesSummaryThunk.fulfilled, (state, action) => {
        state.allEmployeesIncentivesSummary.loading = false;
        state.allEmployeesIncentivesSummary.data = action.payload;
      })
      .addCase(fetchAllEmployeesIncentivesSummaryThunk.rejected, (state, action) => {
        state.allEmployeesIncentivesSummary.loading = false;
        state.allEmployeesIncentivesSummary.error = action.payload;
      })

      .addCase(fetchMonthlyIncentivesDashboardThunk.pending, (state) => {
        state.monthlyIncentivesDashboard.loading = true;
        state.monthlyIncentivesDashboard.error = null;
      })
      .addCase(fetchMonthlyIncentivesDashboardThunk.fulfilled, (state, action) => {
        state.monthlyIncentivesDashboard.loading = false;
        state.monthlyIncentivesDashboard.data = action.payload;
      })
      .addCase(fetchMonthlyIncentivesDashboardThunk.rejected, (state, action) => {
        state.monthlyIncentivesDashboard.loading = false;
        state.monthlyIncentivesDashboard.error = action.payload;
      });
  },
});

// Export actions
export const {
  clearCurrentEmployee,
  clearCreationResponse,
  clearEmployeeErrors,
  clearIncentiveSummary,
  clearEnhancedIncentiveSummary,
  clearDetailedIncentiveHistory,
  clearIncentiveTransactions,
  clearMonthlyIncentives,
  clearAllEmployeesIncentivesSummary,
  clearMonthlyIncentivesDashboard,
  resetAllIncentiveData,
  updateTransactionStatus,
} = employeeSlice.actions;

// Enhanced selectors
export const selectEmployees = (state) => state.employees.employees;
export const selectCurrentEmployee = (state) => state.employees.currentEmployee;
export const selectEmployeeLoading = (state) => state.employees.loading;
export const selectEmployeeError = (state) => state.employees.error;
export const selectLastCreationResponse = (state) => state.employees.lastCreationResponse;

// Basic incentive selectors
export const selectIncentiveSummary = (state) => state.employees.incentiveSummary;

// Enhanced incentive selectors
export const selectEnhancedIncentiveSummary = (state) => state.employees.enhancedIncentiveSummary;
export const selectDetailedIncentiveHistory = (state) => state.employees.detailedIncentiveHistory;
export const selectIncentiveTransactions = (state) => state.employees.incentiveTransactions;
export const selectMonthlyIncentives = (state) => state.employees.monthlyIncentives;

// Admin selectors
export const selectAllEmployeesIncentivesSummary = (state) => state.employees.allEmployeesIncentivesSummary;
export const selectMonthlyIncentivesDashboard = (state) => state.employees.monthlyIncentivesDashboard;

// Computed selectors
export const selectCurrentEmployeeIncentiveStats = (state) => {
  const enhancedSummary = state.employees.enhancedIncentiveSummary.data;
  if (!enhancedSummary) return null;
  
  return {
    currentMonth: enhancedSummary.current_month,
    yearTotals: enhancedSummary.year_totals,
    monthlyBreakdown: enhancedSummary.monthly_breakdown,
    statusBreakdown: enhancedSummary.status_breakdown
  };
};

export const selectRecentTransactions = (state) => {
  return state.employees.detailedIncentiveHistory.list?.slice(0, 5) || [];
};

export default employeeSlice.reducer;